import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  appendLcUserStationParams,
  lcApiBase,
  lcHttpError,
  requireLcUserStationIds,
} from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';
import { ProduitItem, ProduitsPageResult } from './logiciel-caisse-produit.service';

export interface StockLigneParEntrepot {
  entrepot_id: number | null;
  entrepot_libelle: string;
  stock_physique: number;
  stock_disponible: number;
}

interface StockDetailResponse {
  success: boolean;
  lignes?: StockLigneParEntrepot[];
  error?: string;
}

interface GestionGlobaleResponse {
  success: boolean;
  produits?: ProduitItem[];
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
    pages?: number;
  };
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseStockGestionService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  getGestionGlobale(options: {
    actif: boolean;
    page?: number;
    limit?: number;
    byGroup?: boolean;
    entrepotIds?: number[];
    searchRef?: string;
    stockPhysMin?: number;
    stockPhysMax?: number;
  }): Observable<ProduitsPageResult> {
    try {
      requireLcUserStationIds(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }

    const page = options.page ?? 1;
    const limit = options.limit ?? 10;
    let params = new HttpParams()
      .set('actif', String(options.actif))
      .set('page', String(page))
      .set('limit', String(limit))
      .set('by_group', options.byGroup !== false ? 'true' : 'false');

    const ids = (options.entrepotIds ?? []).filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length > 0) {
      params = params.set('entrepot_ids', ids.join(','));
    }

    const q = String(options.searchRef ?? '').trim();
    if (q.length > 0) {
      params = params.set('q', q.slice(0, 120));
    }

    if (options.stockPhysMin != null && Number.isFinite(options.stockPhysMin)) {
      params = params.set('stock_phys_min', String(options.stockPhysMin));
    }
    if (options.stockPhysMax != null && Number.isFinite(options.stockPhysMax)) {
      params = params.set('stock_phys_max', String(options.stockPhysMax));
    }

    params = appendLcUserStationParams(params, this.authService);

    const url = `${lcApiBase(this.authService)}/api/stocks/gestion-globale`;
    return this.http.get<GestionGlobaleResponse>(url, { params }).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.produits)) {
          throw new Error(res?.error || 'Réponse gestion stock invalide');
        }
        const m = res.meta ?? {};
        const total = m.total ?? res.produits.length;
        const lim = m.limit ?? limit;
        const pg = m.page ?? page;
        const pages = m.pages ?? Math.max(1, Math.ceil(total / lim));
        return {
          items: res.produits,
          pagination: {
            page: pg,
            limit: lim,
            total,
            totalPages: pages,
          },
        };
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement du stock global.'))
      )
    );
  }

  getStockDetailParEntrepot(
    produitIds: number[],
    entrepotIds?: number[]
  ): Observable<StockLigneParEntrepot[]> {
    try {
      requireLcUserStationIds(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const uniq = [
      ...new Set(
        produitIds.filter((n) => Number.isFinite(n) && n > 0).map((n) => Math.floor(n))
      ),
    ];
    if (uniq.length === 0) {
      return throwError(() => new Error('Aucun produit valide.'));
    }
    let params = new HttpParams().set('produit_ids', uniq.join(','));
    const ent = (entrepotIds ?? []).filter((n) => Number.isFinite(n) && n > 0);
    if (ent.length > 0) {
      params = params.set('entrepot_ids', ent.join(','));
    }
    params = appendLcUserStationParams(params, this.authService);
    const url = `${lcApiBase(this.authService)}/api/stocks/gestion-globale/detail`;
    return this.http.get<StockDetailResponse>(url, { params }).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.lignes)) {
          throw new Error(res?.error || 'Réponse détail stock invalide');
        }
        return res.lignes;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement du détail stock.'))
      )
    );
  }

  /**
   * GET /api/stocks/stock/produit/:user_id/:station_id/:produits_id
   * Quantité stock physique (somme entrepôts). `code_barre` optionnel pour résoudre l’id caisse.
   */
  getStockPhysiqueByProduit(
    produitsId: number,
    options?: { code_barre?: string | null }
  ): Observable<number> {
    const userId = this.authService.getBackUserId();
    const stationId = this.authService.getBackStationId();
    if (userId == null || stationId == null) {
      return throwError(
        () => new Error('Session caisse incomplète (user/station). Reconnectez le module logiciel caisse.')
      );
    }
    const pid = Number(produitsId);
    if (!Number.isFinite(pid) || pid <= 0) {
      return of(0);
    }
    let params = new HttpParams();
    const cb = options?.code_barre != null ? String(options.code_barre).replace(/\s/g, '').trim() : '';
    if (cb && cb !== '0' && cb !== '-') {
      params = params.set('code_barre', cb);
    }
    const url = `${lcApiBase(this.authService)}/api/stocks/stock/produit/${userId}/${stationId}/${pid}`;
    return this.http
      .get<{ success?: boolean; stock?: { stock_physique?: number } | null }>(url, { params })
      .pipe(
        map((res) => {
          const n = Number(res?.stock?.stock_physique ?? 0);
          return Number.isFinite(n) ? n : 0;
        }),
        catchError(() => of(0))
      );
  }

  /**
   * Totaux stock caisse pour une liste de produits ERP (clé = id ERP).
   */
  getStockPhysiqueByErpProducts(
    products: Array<{ id: number; code_barre?: string | null }>
  ): Observable<Record<number, number>> {
    const list = (products || [])
      .map((p) => ({
        id: Number(p.id),
        code_barre: p.code_barre,
      }))
      .filter((p) => Number.isFinite(p.id) && p.id > 0);
    if (!list.length) {
      return of({});
    }
    return this.authService.ensureAuthenticatedSession().pipe(
      switchMap((ok) => {
        if (!ok) {
          return throwError(() => new Error('Session logiciel caisse indisponible.'));
        }
        return forkJoin(
          list.map((p) =>
            this.getStockPhysiqueByProduit(p.id, { code_barre: p.code_barre }).pipe(
              map((qty) => ({ id: p.id, qty })),
              catchError(() => of({ id: p.id, qty: 0 }))
            )
          )
        );
      }),
      map((rows) => {
        const out: Record<number, number> = {};
        for (const r of rows) {
          out[r.id] = r.qty;
        }
        return out;
      }),
      catchError(() => {
        const empty: Record<number, number> = {};
        for (const p of list) {
          empty[p.id] = 0;
        }
        return of(empty);
      })
    );
  }
}
